import { useEffect, useMemo, useState } from 'react'
import { supabase } from '@/lib/supabase'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { toast } from '@/components/ui/use-toast'

interface Props {
  value: string
  onValueChange: (value: string) => void
  ariaLabel?: string
}

interface JobRow {
  location: string | null
}

function normalizeLocation(raw: string): string {
  let s = (raw || '').trim()
  if (!s) return ''

  // Common cleanups
  s = s.replace(/,\s*(USA|U\.S\.A\.|US)$/i, '') // drop country suffix
  s = s.replace(/\s*,\s*/g, ', ') // normalize comma spacing

  // Standardize Remote
  if (/^remote$/i.test(s)) return 'Remote'

  // Normalize Washington DC variants
  if (/washington/i.test(s) && /d\.?c\.?/i.test(s)) {
    return 'Washington, DC'
  }

  // Remove duplicated state if present like "Richmond, VA, VA"
  s = s.replace(/,\s*([A-Za-z]{2}),\s*\1$/i, ', $1')

  return s
}

export function JobLocationsFromJobs({ value, onValueChange, ariaLabel }: Props) {
  const [locations, setLocations] = useState<string[]>([])
  const [loading, setLoading] = useState<boolean>(true)

  useEffect(() => {
    let isMounted = true
    async function fetchLocations() {
      try {
        setLoading(true)
        const { data, error } = await supabase
          .from('jobs')
          .select('location')
          .eq('status', 'active')

        if (error) throw error

        const normalized = (data as JobRow[])
          .map((r) => normalizeLocation(r.location ?? ''))
          .filter(Boolean)

        // Unique + sort, put Remote at top if present
        const set = new Set(normalized)
        const unique = Array.from(set).sort((a, b) => a.localeCompare(b))
        const remoteIndex = unique.findIndex((l) => l.toLowerCase() === 'remote')
        if (remoteIndex > 0) {
          const [remote] = unique.splice(remoteIndex, 1)
          unique.unshift(remote)
        }

        if (isMounted) {
          setLocations(unique)
          console.log(`[JobLocationsFromJobs] Loaded ${unique.length} formatted locations`)
        }
      } catch (err: any) {
        console.error('Failed to load locations:', err)
        toast({
          title: 'Failed to load locations',
          description: err.message ?? String(err),
          variant: 'destructive',
        })
      } finally {
        if (isMounted) setLoading(false)
      }
    }

    // Significantly defer location loading to break critical request chain
    const timer = setTimeout(() => {
      fetchLocations()
    }, 1000)
    
    return () => {
      clearTimeout(timer)
      isMounted = false
    }
  }, [])

  const selectedValue = useMemo(() => {
    if (value === 'all') return value
    if (locations.includes(value)) return value
    return 'all' // fallback if previous value no longer present
  }, [value, locations])

  return (
    <Select
      value={selectedValue}
      onValueChange={onValueChange}
      disabled={loading && locations.length === 0}
    >
      <SelectTrigger className="w-full" aria-label={ariaLabel || "Filter by location"}>
        <SelectValue placeholder={loading ? 'Loading locations...' : 'All Locations'} />
      </SelectTrigger>
      <SelectContent className="z-50 bg-background w-[--radix-select-trigger-width]">
        <SelectItem value="all">All Locations</SelectItem>
        {locations.map((loc) => (
          <SelectItem key={loc} value={loc}>
            {loc}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
